/**
 * Live drawing drafts — peer's in-progress strokes rendered in real time
 * as they're drawn, before the author finishes the gesture.
 *
 * Wire protocol (extends AGENTS.md §9):
 *   - `annotation/draft.update` (UNRELIABLE channel) — emitted by the
 *     author at ~30 fps as they drag. Payload carries the full
 *     in-progress geometry so a dropped packet is harmless: the next
 *     packet supersedes it. The `id` is the UUID the author already
 *     reserved for the eventual annotation row, so peers can correlate
 *     the draft to the persisted row when it arrives.
 *   - `annotation/draft.end` (UNRELIABLE) — emitted when the author
 *     cancels the gesture (pointercancel) so peers drop the draft
 *     immediately rather than waiting for the prune timer.
 *   - The existing `annotation/draw.create` (RELIABLE) lands the
 *     finalized stroke. The sync hook removes the matching draft on
 *     receipt — the persisted row supersedes the transient one with
 *     no visual flicker since both have identical geometry.
 *
 * Self-loopback: LiveKit's `publishData` does NOT deliver to the local
 * participant, so the author never receives their own draft envelopes
 * — no echo handling needed.
 *
 * Stale prune: 5 s after the last update with no `draw.create` arrives
 * (cancelled stroke + dropped end packet, network blip, peer crash),
 * the draft is removed.
 */
import { useEffect } from "react";
import { create } from "zustand";

import type { CommandBus } from "./commandBus";
import type {
  AnnotationGeometry,
  AnnotationKind,
} from "./types";

export type LiveDraft = {
  /** = future `Annotation.id` so the persisted row supersedes the
   *  draft cleanly when `draw.create` arrives. */
  id: string;
  /** `null` for a live-overlay draft (drawn on the video); set to the
   *  Evidence id when the draft is happening inside the
   *  EvidenceAnnotator. Consumers filter by their own scope. */
  evidence_id: string | null;
  drawer_id: string;
  kind: AnnotationKind;
  geometry: AnnotationGeometry;
  color: string;
  stroke_width: number;
  /** Last update timestamp — used by the prune timer. */
  ts: number;
};

type Store = {
  byId: Record<string, LiveDraft>;
  upsert: (d: LiveDraft) => void;
  remove: (id: string) => void;
  pruneStale: (maxAgeMs: number) => void;
};

export const useLiveDraftsStore = create<Store>((set) => ({
  byId: {},
  upsert: (d) => set((s) => ({ byId: { ...s.byId, [d.id]: d } })),
  remove: (id) =>
    set((s) => {
      if (!(id in s.byId)) return s;
      const next = { ...s.byId };
      delete next[id];
      return { byId: next };
    }),
  pruneStale: (maxAgeMs) =>
    set((s) => {
      const now = Date.now();
      const keep: Record<string, LiveDraft> = {};
      let changed = false;
      for (const [id, d] of Object.entries(s.byId)) {
        if (now - d.ts < maxAgeMs) keep[id] = d;
        else changed = true;
      }
      return changed ? { byId: keep } : s;
    }),
}));

export type DraftUpdatePayload = {
  id: string;
  evidence_id: string | null;
  kind: AnnotationKind;
  geometry: AnnotationGeometry;
  color: string;
  stroke_width: number;
};


/** Subscribe to inbound peer drafts and lifecycle envelopes. Mount once
 *  per session (in InRoomAnnotation). The store is then read by both
 *  AnnotationOverlay (live) and EvidenceAnnotator (collab) — each
 *  filters by its own scope. */
export function useLiveDraftsSync(bus: CommandBus | null): void {
  useEffect(() => {
    if (!bus) return;
    const offUpdate = bus.on<DraftUpdatePayload, void>(
      "annotation/draft.update",
      ({ from, payload }) => {
        useLiveDraftsStore.getState().upsert({
          id: payload.id,
          evidence_id: payload.evidence_id ?? null,
          drawer_id: from.id,
          kind: payload.kind,
          geometry: payload.geometry,
          color: payload.color,
          stroke_width: payload.stroke_width,
          ts: Date.now(),
        });
      },
    );
    const offEnd = bus.on<{ id: string }, void>(
      "annotation/draft.end",
      ({ payload }) => useLiveDraftsStore.getState().remove(payload.id),
    );
    // When the persisted row lands, drop the matching draft. The
    // useAnnotationStore / EvidenceAnnotator local set already has the
    // final geometry — leaving the draft in place would render a
    // duplicate.
    const offCreate = bus.on<{ annotation: { id: string } }, void>(
      "annotation/draw.create",
      ({ payload }) => useLiveDraftsStore.getState().remove(payload.annotation.id),
    );
    // Periodic prune for orphans (cancelled strokes + dropped end packet,
    // peer crashed mid-stroke, etc.).
    const interval = window.setInterval(() => {
      useLiveDraftsStore.getState().pruneStale(5_000);
    }, 1_000);
    return () => {
      offUpdate();
      offEnd();
      offCreate();
      window.clearInterval(interval);
    };
  }, [bus]);
}


/** Author-side throttled sender. Construct one per gesture (mint a
 *  fresh UUID on pointerdown, hand it to this factory along with the
 *  bus + scope). Call `send(geometry)` from pointermove; the helper
 *  caps publish frequency to ~30 fps regardless of how often it's
 *  called. Call `end()` on pointercancel to drop the peer's draft
 *  immediately; on pointerup, just let the existing `draw.create`
 *  envelope supersede the draft and DON'T call `end()` — that would
 *  remove the draft a few ms before the persisted row lands and the
 *  peer would see a flicker. */
export function makeDraftBroadcaster(
  bus: CommandBus | null,
  args: {
    id: string;
    evidence_id: string | null;
    kind: AnnotationKind;
    color: string;
    stroke_width: number;
  },
): { send: (geometry: AnnotationGeometry) => void; end: () => void } {
  let lastSentAt = 0;
  const noop = { send: () => {}, end: () => {} };
  if (!bus) return noop;
  return {
    send: (geometry) => {
      const now = Date.now();
      // ~20 fps throttle. We started at ~30 fps and observed perceived
      // lag on slower connections / devices because each inbound
      // packet triggers a Zustand re-render of the SVG overlay.
      // Halving the broadcast rate roughly halves render cost on the
      // peer side and keeps the perceived smoothness — the eye reads
      // 20 fps as "live" for a continuous gesture. Adjust upward only
      // if smoothness regresses.
      if (now - lastSentAt < 50) return;
      lastSentAt = now;
      void bus
        .send({
          topic: "annotation",
          type: "draft.update",
          payload: {
            id: args.id,
            evidence_id: args.evidence_id,
            kind: args.kind,
            geometry,
            color: args.color,
            stroke_width: args.stroke_width,
          } satisfies DraftUpdatePayload,
          requires_ack: false,
          reliable: false,
        })
        .catch(() => {});
    },
    end: () => {
      void bus
        .send({
          topic: "annotation",
          type: "draft.end",
          payload: { id: args.id },
          requires_ack: false,
          reliable: false,
        })
        .catch(() => {});
    },
  };
}
